import axios from 'axios'

import { DateFormat } from '../../@paco/types/dateFormatTypes'

import { checkIntegrityOfShiftSnapshot, transformShift } from '../../models'
import {
    addDays,
    formatDate,
    getEndOfWorkDay,
    getStartOfWorkDay,
} from '../date'

const getShiftsByDate = async (startDate, endDate, filter) => {
    const includes = ['department', 'department.group']

    const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/shifts/`,
        {
            params: {
                include: includes.join(','),
                ...(filter && { search: filter.employeeSearch }),
                startDate: formatDate(getStartOfWorkDay(startDate), DateFormat.apiDateTime),
                endDate: formatDate(getEndOfWorkDay(endDate), DateFormat.apiDateTime),
                departments: filter.departments ? JSON.stringify(filter.departments) : undefined,
            },
        },
    )

    response.data.map(transformShift)

    return response
}

const getShift = (shiftId) => {
    const includes = ['department', 'department.group', 'plannedUsers', 'registeredUsers', 'registeredUsers.employmentType',
        'plannedUsers.experience', 'shiftPlannings', 'shiftPlannings.user.person', 'shiftPlannings.user.experience', 'shiftPlannings.user.employmentType',
        'shiftPlannings.comments', 'previouslyPlannedUsers', 'previouslyPlannedUsers.employmentType', 'shiftPlannings.comments.owner',
        'shiftPlannings.comments.owner.employmentType', 'shiftPlannings.comments.owner.experience', 'shiftPlannings.comments.owner.person', 'temporaryWorkers',
        'temporaryWorkers.comments', 'temporaryWorkers.comments.owner', 'temporaryWorkers.comments.owner.person']

    return axios.get(
        `${process.env.REACT_APP_API_URL}/shifts/${shiftId}`,
        {
            params: {
                include: includes.join(','),
            },
        },
    )
}

const getShiftSnapshot = async (shiftId) => {
    const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/shifts/${shiftId}/snapshot`,
    )

    if (! checkIntegrityOfShiftSnapshot(response.data)) {
        throw new TypeError(`Snapshot ${response.data.id} type mismatch`)
    }

    return response
}

const getSimilarShifts = shiftId => axios.get(
    `${process.env.REACT_APP_API_URL}/shifts/similar/${shiftId}`,
)

const delUserFromShift = (shiftId, userId) => axios.delete(
    `${process.env.REACT_APP_API_URL}/shift/plannings/${shiftId}/${userId}`,
)

const generateNotifyBody = (data) => {
    const {
        departments, employees, subject, body,
    } = data

    const departmentsData = departments && departments.map(id => ({ id, type: 'department' }))
    const employeesData = employees && employees.map(id => ({ id, type: 'user' }))

    return {
        type: 'notifications',
        attributes: {
            subject,
            body,
        },
        relationships: {
            ...(departmentsData && {
                departments: {
                    data: departmentsData,
                },
            }),
            ...(employeesData && {
                users: {
                    data: employeesData,
                },
            }),
        },
    }
}

const postNotifyDepartments = (data) => {
    const dataBody = generateNotifyBody(data)

    return axios.post(`${process.env.REACT_APP_API_URL}/departments/notify`, { data: dataBody })
}

const postNotifyEmployees = (data) => {
    const dataBody = generateNotifyBody(data)

    return axios.post(`${process.env.REACT_APP_API_URL}/users/notify`, { data: dataBody })
}

const getSpecialDays = (year = 2019, start, end) => {
    let startDate = `${year - 1}-12-31 04:00:00`
    let endDate = `${year + 1}-01-01 03:59:00`

    if (start && end) {
        startDate = getStartOfWorkDay(startDate)
        endDate = getEndOfWorkDay(endDate)
    }

    return axios.get(`${process.env.REACT_APP_API_URL}/special-days`, {
        params: {
            orderByDate: JSON.stringify({ date: 'DESC' }),
            startDate: formatDate(startDate, DateFormat.apiDateTime),
            endDate: formatDate(endDate, DateFormat.apiDateTime),
        },
    })
}

const getSpecialDayTypes = () => axios.get(`${process.env.REACT_APP_API_URL}/special-days/types`)

const generateSpecialDayBody = (data) => ({
    type: 'specialDay',
    ...(data.id && { id: data.id }),
    attributes: {
        type: data.type,
        date: data.date,
        description: data.description,
        ...(data.isVisibleInEmployeeApp !== undefined && { isVisibleInEmployeeApp: data.isVisibleInEmployeeApp }),
    },
})

const postSpecialDay = (data) => {
    const body = generateSpecialDayBody(data)

    return axios.post(`${process.env.REACT_APP_API_URL}/special-days`, { data: body })
}

const patchSpecialDay = (data) => {
    const body = generateSpecialDayBody(data)

    return axios.patch(`${process.env.REACT_APP_API_URL}/special-days/${data.id}`, { data: body })
}

const delSpecialDay = (id) => axios.delete(`${process.env.REACT_APP_API_URL}/special-days/${id}`)

const getPreferToWorkTimeSlots = (
    startDate,
    endDate,
    userUuids = [],
    filter = {},
) => {
    const { search, departments } = filter

    return axios.get(`${process.env.REACT_APP_API_URL}/prefer-to-work-time-slots`, {
        params: {
            orderByStart: JSON.stringify({ start: 'DESC' }),
            userUuids: userUuids.length > 0 ? JSON.stringify(userUuids) : undefined,
            startDate: formatDate(getStartOfWorkDay(startDate), DateFormat.apiDateTime),
            endDate: formatDate(getEndOfWorkDay(endDate), DateFormat.apiDateTime),
            include: ['user', 'user.person'].join(','),
            ...(search && { search }),
            ...(departments && { departments }),
        },
    })
}

const getUnavailableWorkTimeSlots = (
    startDate,
    endDate,
    userUuids = [],
    filter = {},
) => {
    const { search, departments } = filter

    const start = formatDate(addDays(startDate, -1), 'yyyy-MM-dd')
    const end = formatDate(addDays(endDate, 1), 'yyyy-MM-dd')
    const urlParams = `startDate=${start}&endDate=${end}`

    return axios.get(`${process.env.REACT_APP_API_URL}/unavailable-to-work-time-slots?${urlParams}`, {
        params: {
            orderByStart: JSON.stringify({ start: 'DESC' }),
            userUuids: userUuids.length > 0 ? JSON.stringify(userUuids) : undefined,
            include: ['user', 'user.person'].join(','),
            ...(search && { search }),
            ...(departments && { departments }),
        },
    })
}


export {
    delSpecialDay,
    delUserFromShift,
    getPreferToWorkTimeSlots,
    getShift,
    getShiftsByDate,
    getShiftSnapshot,
    getSimilarShifts,
    getSpecialDays,
    getSpecialDayTypes,
    getUnavailableWorkTimeSlots,
    patchSpecialDay,
    postNotifyDepartments,
    postNotifyEmployees,
    postSpecialDay,
}
